<script>
import PageLoading from "./../../../partials/PageLoading";
import KolonneSettingsEmployeesListItem from "./KolonneSettingsEmployeesListItem.vue";
import KolonneSettingsEmployeesRankingLegend from "./KolonneSettingsEmployeesRankingLegend.vue";

export default {
  name: "KolonneSettingsEmployeesModal",

  components: {
    PageLoading,
    KolonneSettingsEmployeesListItem,
    KolonneSettingsEmployeesRankingLegend,
  },

  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    week: {
      type: String | null,
      default: null,
    },
    startdate: {
      type: String, // format 'YYYY-MM-DD'
      default: '',
    },
    selectedKolonneId: {
      type: Number | String,
      default: ''
    },
    selectedKolonneNumber: {
      type: undefined | Number | String,
      default: ''
    },
    selectedEmployeeIds: {
      type: Array,
      default: () => [],
    },
    availableEmployees: {
      type: Array,
      default: () => [],
    },
    kolonneContent: {
      type: Array,
      default: () => [],
    },
  },

  model: {
    prop: "showDialog",
    event: "showchange"
  },

  data() {
    return {
      isLoading: false,
      saveEmployeesError: undefined,

      isRefreshingData: false,
      refreshingDataError: undefined,

      employeeSearchtext: "",
      selectedEmployees: [],
    };
  },

  mounted () {
    this.createSelectedEmployees();
  },

  computed: {
    showDialog: {
      get () {
        if (this.isVisible) {
          this.onOpenDialog();
        }
        return this.isVisible;
      },
      set (value) {
        this.$emit("showchange", value);
      }
    },

    filteredEmployees () {
      return this.availableEmployees
        .filter(employee => {
          const searchtext = this.employeeSearchtext.toLowerCase();

          return employee.firstName.toLowerCase().includes(searchtext) ||
                  employee.lastName.toLowerCase().includes(searchtext);
        })
        .sort((a,b) => {
          return a.lastName.localeCompare(b.lastName);
        })
    }
  },

  methods: {
    createSelectedEmployees () {
      this.selectedEmployees = this.selectedEmployeeIds
        .map(employeeId => {
          const employee = this.availableEmployees.find(availableEmployee => {
            return availableEmployee.id === employeeId
          })

          return {
            active: employee != null,
            ...(employee ?? {}),
          }
        });
    },

    /**
     * @param {{id: string | number, firstName: string, lastName: string}} employee
     */
    isSelectedEmployee(employee) {
      return !!this.selectedEmployees.find(selectedEmployee => {
        return selectedEmployee.id === employee.id;
      });
    },

    onChangeEmployeeSelected (employeeId, isSelected) {
      isSelected
        ? this.selectEmployeeById(employeeId)
        : this.unselectEmployeeById(employeeId)
    },

    selectEmployeeById (employeeId) {
      console.log('Should select employee by ID', { employeeId });

      const employee = this.availableEmployees.find(employee => employee.id === employeeId);
      console.log('Attempted to find employee', { employee });

      if (employee && !this.isSelectedEmployee(employee)) {
        console.log('Employee is not yet selected. Adding it to the selection', {
          employee,
          selectedEmployees: this.selectedEmployees,
        });

        this.selectedEmployees.push({
          active: true,
          ...employee,
        })
      }
    },

    unselectEmployeeById (employeeId) {
      this.selectedEmployees = this.selectedEmployees.filter(employee => {
        return employee.id !== employeeId
      });

    },

    onOpenDialog () {
      this.showDialog = true;
    },

    onCloseDialog () {
      this.showDialog = false;
    },

    onSaveEmployees() {
      this.isLoading = true
      this.saveEmployeesError = undefined

      const selectedEmployeeIds = this.selectedEmployees.flatMap(employee => employee.id);

      this.$api
        .clearParams()
        .put(`kolonne-settings/${this.selectedKolonneId}`, {
          employees_ids: selectedEmployeeIds,
        })
        .then(response => {
          window.app.$helpers.logger("Updating employees was a success …");
          this.$emit("saved", response.data.data);
        })
        .catch(error => {
          window.app.$helpers.logger(error);
          this.saveEmployeesError = error
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    onRefreshEmployeeData () {
      this.isRefreshingData = true
      this.refreshingDataError = undefined

      this.$api
        .clearParams()
        .addParam('refresh', 1)
        .addParam('startdate', this.startdate)
        .get('geocapture/employees')
        .then(response => {
          window.app.$helpers.logger("Refreshing employee data successful", {
            employees: response.data.data,
          });
          this.$emit("refreshedEmployees", response.data.data);
        })
        .catch(error => {
          window.app.$helpers.logger('Failed to refresh employee data', { error });
          this.refreshingDataError = error
        })
        .finally(() => {
          this.isRefreshingData = false;
        });
    },
  },
};
</script>

<template>
  <div class="dialog" :class="{ 'active' : showDialog }">
    <div class="dialog--content u-extra-medium-padding--bottom c-employee-modal">
      <h4 class="u-extra-small-margin--bottom">
        {{ $t('kolonne.employees.title.modal') }}

        <div v-show="isLoading">
          <div class="loading-spinner-wrapper">
            <PageLoading position="absolute" right/>
          </div>
        </div>
      </h4>

      <h5 class="u-color--primary u-extra-medium-margin--bottom">
        {{ $t('kolonne.title') }} {{ selectedKolonneNumber }}
        <template v-if="week"> — KW {{ week }}</template>
      </h5>

      <p v-if="selectedKolonneNumber == '' || selectedKolonneNumber == null" class="is-error">
        {{ $t('kolonne.error.nokolonne') }}
      </p>

      <div v-else>
        <div class="grid-container fluid" v-if="saveEmployeesError">
          <div class="grid-x grid-margin-x align-center align-middle">
            <div class="cell small">
              <div class="callout alert text-center">
                {{ $t('kolonne.employees.saveerror') }}: {{ saveEmployeesError.message }}
              </div>
            </div>
          </div>
        </div>

        <div class="grid-x grid-margin-x">
          <div class="cell small-6">
            <h6 class="u-color--dark-gray u-no-margin--bottom u-extra-small-padding--bottom">
              {{ $t('kolonne.employees.selected') }} ({{ selectedEmployees.length }})
            </h6>

            <p v-if="selectedEmployees.length === 0">
              {{ $t('kolonne.employees.noemployees.selected') }}
            </p>
          </div>
          <div class="cell small-6 text-right">
            <button class="button u-text-small" @click="onSaveEmployees">
              {{ $t('save') }}
            </button>
          </div>
        </div>

        <KolonneSettingsEmployeesListItem
          v-for="employee in selectedEmployees"
          :key="`selected-${employee.id}`"
          :active="true"
          :employee="employee"
          :startdate="startdate"
          :kolonne-content="kolonneContent"
          :hide-ranking="true"
          @activeChanged="onChangeEmployeeSelected"
        />

        <p v-if="this.availableEmployees.length === 0" class="u-color--alert text-center h6">
          {{ $t('kolonne.message.noemployees') }}
        </p>

        <div v-else>
          <div class="grid-container fluid" v-if="refreshingDataError">
            <div class="grid-x grid-margin-x align-center align-middle">
              <div class="cell small">
                <div class="callout alert text-center">
                  {{ $t('kolonne.employees.refresherror') }}: {{ refreshingDataError.message }}
                </div>
              </div>
            </div>
          </div>

          <div class="grid-x grid-margin-x align-middle u-extra-medium-padding--top">
            <div class="cell small-6">
              <h6 class="u-color--dark-gray u-no-margin--bottom">
                {{ $t('kolonne.employees.available') }} ({{ availableEmployees.length }})
              </h6>
            </div>

            <div class="cell small-6">
              <div class="grid-x grid-margin-x align-middle">
                <div class="cell auto">
                  <!-- this is a spacer column to push the loading spinner to the right … -->
                </div>
                <div class="cell small-2 text-right">
                  <div class="loading-spinner-wrapper" v-if="isRefreshingData">
                    <PageLoading class="loading-spinner--small u-no-margin" />
                  </div>
                </div>
                <div class="cell shrink text-right">
                  <button
                    class="button button--small outline secondary"
                    :class="{'disabled': isRefreshingData }"
                    @click="onRefreshEmployeeData"
                  >
                    {{ $t('kolonne.settings.data.refresh') }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <p class="u-text-medium u-extra-small-margin--bottom">
            {{ $t('kolonne.employees.source.geocapture.hint') }}
          </p>

          <div class="grid-x grid-margin-x align-middle">
            <div class="cell small-12">
              <div class="form-group form-group--search u-extra-small-margin--bottom">
                <input
                  type="text"
                  name="employeefilter"
                  class="form-control form-control--small form-control--search"
                  :placeholder="$t('search')"
                  v-model="employeeSearchtext" />
              </div>
            </div>
          </div>

          <KolonneSettingsEmployeesRankingLegend class="u-extra-medium-margin--bottom" />

          <div class="grid-x grid-margin-x align-middle">
            <KolonneSettingsEmployeesListItem
              v-for="employee in filteredEmployees"
              :key="employee.id"
              :active="isSelectedEmployee(employee)"
              :employee="employee"
              :startdate="startdate"
              :kolonne-content="kolonneContent"
              :selected-kolonne-id="selectedKolonneId"
              @activeChanged="onChangeEmployeeSelected"
            />
          </div>
        </div>
      </div>

      <button class="close-button" @click.prevent="onCloseDialog">
          <span aria-hidden="true">&times;</span>
      </button>
    </div>
  </div>
</template>

